import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import queryClient from '@/lib/queryClient'
import {
  buildAdminQueryRoot,
  buildScopedAdminQueryKey,
  type AdminQueryKey,
  type AdminQueryRoot,
  type OrganizationIdInput,
} from '@/lib/organization-scoping'
import { queryOptions, useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query'
import {
  getGoogleTagManagerIntegration,
  removeGoogleTagManagerIntegration,
  updateGoogleTagManagerIntegration,
  type GoogleTagManagerOrganizationScope,
} from './google-tag-manager.api'
import type {
  GoogleTagManagerIntegration,
  GoogleTagManagerIntegrationInput,
} from './google-tag-manager.types'

interface OrganizationScope {
  organizationId?: OrganizationIdInput
}

/** The key entries the fetcher reads; the scoped key builder below sets them. */
type GoogleTagManagerIntegrationQueryKey = [AdminQueryRoot & GoogleTagManagerOrganizationScope]

/** Query options a caller may override; the key and fetcher stay owned by this module. */
type QueryParams<TOptions> = Partial<Omit<TOptions, 'queryKey' | 'queryFn'>>

export const googleTagManagerQueryKeys = {
  all: ({ organizationId }: OrganizationScope = {}): AdminQueryKey => [
    buildAdminQueryRoot({ entity: 'googleTagManager' }, { organizationId }),
  ],
  integration: ({ organizationId }: OrganizationScope = {}): GoogleTagManagerIntegrationQueryKey =>
    buildScopedAdminQueryKey(googleTagManagerQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'integration',
    }),
}

export const googleTagManagerQueries = {
  integration: ({ organizationId }: OrganizationScope = {}) =>
    queryOptions({
      queryKey: googleTagManagerQueryKeys.integration({ organizationId }),
      queryFn: getGoogleTagManagerIntegration,
    }),
}

type GoogleTagManagerIntegrationParams = OrganizationScope &
  QueryParams<ReturnType<typeof googleTagManagerQueries.integration>>

function syncIntegration(organizationId: number, integration: GoogleTagManagerIntegration) {
  queryClient.setQueryData(googleTagManagerQueryKeys.integration({ organizationId }), integration)
}

export function useGoogleTagManagerIntegration({
  organizationId,
  ...params
}: GoogleTagManagerIntegrationParams = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...googleTagManagerQueries.integration({ organizationId: resolvedOrganizationId }),
    ...params,
  })
}

export function useSuspenseGoogleTagManagerIntegration({
  organizationId,
  ...params
}: GoogleTagManagerIntegrationParams = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useSuspenseQuery({
    ...googleTagManagerQueries.integration({ organizationId: resolvedOrganizationId }),
    ...params,
  })
}

export function useUpdateGoogleTagManagerIntegrationMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (values: GoogleTagManagerIntegrationInput) =>
      updateGoogleTagManagerIntegration({ organizationId, ...values }),
    onSuccess: (integration) => syncIntegration(organizationId, integration),
  })
}

export function useRemoveGoogleTagManagerIntegrationMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: () => removeGoogleTagManagerIntegration({ organizationId }),
    onSuccess: (integration) => syncIntegration(organizationId, integration),
  })
}
